import moment from 'moment-timezone'
import { dateTime as SharedDateTime } from '@blocktickets/shared-utils'

import {
  formatCurrency,
  formatDateTime,
  formatNumber,
  getNowInTimezone,
  getTimezoneDate,
} from '../../../utilities/helpers'

import {
  assignedSeatCount,
  closedAvailabilityPeriod,
} from '../offerAvailability'
import { resolveTicketBreakdown } from '../ticketBreakdown'

const EMPTY = '—'

function round2(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0
}

function pricingLevels(offer) {
  const raw = offer?.am_pricing_objects
  const list = Array.isArray(raw) ? raw : raw ? Object.values(raw) : []
  return [...list].sort((a, b) => {
    const numA = parseInt(String(a?.name || '').replace(/\D/g, ''), 10)
    const numB = parseInt(String(b?.name || '').replace(/\D/g, ''), 10)
    if (!Number.isFinite(numA) || !Number.isFinite(numB)) return 0
    return numA - numB
  })
}

function sortOffers(offers, timezone, isSortingOpen) {
  const standardId = offers[0]?.id
  const now = getNowInTimezone(timezone)

  return [...offers].sort((a, b) => {
    const activeAPeriod = a.availability?.find((period) => period.active)
    const activeBPeriod = b.availability?.find((period) => period.active)

    if (!activeAPeriod?.ends || !activeBPeriod?.ends) {
      if (!activeAPeriod?.ends && !activeBPeriod?.ends) return 0
      if (!activeAPeriod?.ends) return 1
      return -1
    }

    const aEndDate = getTimezoneDate(activeAPeriod.ends, timezone)
    const bEndDate = getTimezoneDate(activeBPeriod.ends, timezone)

    if (aEndDate.isSameOrAfter(now) && bEndDate.isSameOrAfter(now)) {
      const aStartDate = getTimezoneDate(activeAPeriod.starts, timezone)
      const bStartDate = getTimezoneDate(activeBPeriod.starts, timezone)
      if (isSortingOpen) {
        if (
          !a.isSoldOut &&
          !b.isSoldOut &&
          aStartDate.isSameOrBefore(now) &&
          bStartDate.isSameOrBefore(now)
        ) {
          if (a.id === standardId) return 1
          return -1
        }
        return aStartDate - bStartDate
      }
      return aStartDate - bStartDate
    }

    if (aEndDate.isSameOrAfter(now) && bEndDate.isBefore(now)) return -1
    if (aEndDate.isBefore(now) && bEndDate.isSameOrAfter(now)) return 1
    return aEndDate - bEndDate
  })
}

/** Open offers first, then exclusive, matching the Offers page. Connected offers follow their parent. */
export function offersInPageOrder(offers, timezone) {
  const list = offers || []
  const open = sortOffers(
    list.filter((offer) => offer?.inventoryType?.toLowerCase() === 'open'),
    timezone,
    true
  )
  const exclusive = sortOffers(
    list.filter((offer) => offer?.inventoryType === 'exclusive'),
    timezone,
    false
  )
  const ordered = []
  const seen = new Set()
  const push = (offer) => {
    if (!offer || seen.has(offer.id)) return
    seen.add(offer.id)
    ordered.push(offer)
  }
  for (const offer of [...open, ...exclusive]) {
    push(offer)
    for (const connected of offer.connected_offers || []) push(connected)
  }
  for (const offer of list) push(offer)
  return ordered
}

function saleBound(offers, offer, property) {
  if (!offer?.availability?.length) return

  const selectedPeriod =
    offer.availability.find((period) => period.active) ||
    closedAvailabilityPeriod(offer.availability)
  if (!selectedPeriod) return

  if (selectedPeriod?.name?.includes('General On-sale')) {
    const standardAdmissionAvailability = offers?.[0]?.availability
    if (!standardAdmissionAvailability?.length) {
      return selectedPeriod?.[property]
    }

    let standardAdmissionPeriod = standardAdmissionAvailability.find(
      (period) => period.name === selectedPeriod.name
    )

    if (!standardAdmissionPeriod) {
      const generalOnsalePeriods = offer.availability.filter((period) =>
        period?.name?.includes('General On-sale')
      )
      const periodIndex = generalOnsalePeriods.findIndex(
        (period) => period === selectedPeriod
      )
      if (periodIndex >= 0) {
        const standardGeneralOnsalePeriods =
          standardAdmissionAvailability.filter((period) =>
            period?.name?.includes('General On-sale')
          )
        standardAdmissionPeriod =
          standardGeneralOnsalePeriods[periodIndex] ??
          standardAdmissionAvailability[periodIndex]
      }
    }

    return standardAdmissionPeriod?.[property] ?? selectedPeriod?.[property]
  }

  return selectedPeriod[property]
}

function timezoneAbbreviation(date, timezone) {
  const name = SharedDateTime.ensureIanaTimeZone(timezone, '')
  if (!name) {
    return typeof timezone?.abbr === 'string' ? timezone.abbr : ''
  }
  try {
    const instant = date ? moment(date) : moment()
    if (!instant.isValid()) return ''
    return moment.tz(instant, name).format('z')
  } catch {
    return typeof timezone?.abbr === 'string' ? timezone.abbr : ''
  }
}

function formatWindowPart(value, timezone) {
  const zoned = getTimezoneDate(value, timezone)
  if (!zoned?.isValid?.()) return ''
  const abbr = timezoneAbbreviation(value, timezone)
  return `${zoned.format('MMM D, h:mm A')}${abbr ? ` ${abbr}` : ''}`
}

function accessCodesFor(offer) {
  const raw = []
  if (Array.isArray(offer?.accessCodes)) raw.push(...offer.accessCodes)
  if (offer?.accessCode) raw.push(...String(offer.accessCode).split(','))
  const codes = []
  for (const code of raw) {
    const trimmed = String(code || '').trim()
    if (trimmed && !codes.includes(trimmed)) codes.push(trimmed)
  }
  return codes
}

export function accessCodeLabel(codes) {
  if (!codes?.length) return ''
  if (codes.length === 1) return codes[0]
  return `${codes.length} codes · ${codes[0]}`
}

export function ticketLimitLabel(offer, globalTicketLimit) {
  if (offer?.limit) return String(offer.limit)
  const parts = []
  if (
    offer?.hasMinQuantity &&
    offer?.minQuantity !== '' &&
    offer?.minQuantity != null
  ) {
    parts.push(`Min ${offer.minQuantity}`)
  }
  if (
    offer?.hasMaxQuantity &&
    offer?.maxQuantity !== '' &&
    offer?.maxQuantity != null
  ) {
    parts.push(`Max ${offer.maxQuantity}`)
  }
  if (
    offer?.hasMultipleOfQuantity &&
    offer?.multipleOf !== '' &&
    offer?.multipleOf != null
  ) {
    parts.push(`Multiple of ${offer.multipleOf}`)
  }
  if (parts.length) return parts.join(', ')
  if (globalTicketLimit) return `None (global limit of ${globalTicketLimit})`
  return 'None'
}

function inventorySet(offer, context) {
  if (String(offer?.inventoryType || '').toLowerCase() === 'open') {
    return { name: '', seats: null, label: '' }
  }
  const seats = assignedSeatCount(offer, context)
  const name = offer?.name || ''
  if (seats == null) {
    return {
      name,
      seats: null,
      label: name ? `${name} • No seats assigned` : '',
    }
  }
  const noun = seats === 1 ? 'seat' : 'seats'
  return {
    name,
    seats,
    label: `${name} • ${formatNumber(seats)} ${noun}`,
  }
}

function zeroMoney(seats) {
  return {
    basePrice: 0,
    offerPrice: 0,
    facilityFee: 0,
    serviceFee: 0,
    processingFee: 0,
    salesTax: 0,
    chargeBackProtection: 0,
    totalToBuyer: 0,
    payout: 0,
    maxPayout: 0,
    seats,
  }
}

function priceLevelRow(level, offer, event, getOfferPrice, levels, index) {
  const seats = Number.parseInt(level?.seats, 10)
  const seatCount = Number.isFinite(seats) ? seats : 0
  const offerPrice = getOfferPrice
    ? getOfferPrice(levels, index, offer?.pricing_option, offer?.discount)
    : level?.offerPrice

  if (offer?.freeOffer) {
    return {
      name: level?.name || '',
      color: level?.color || '',
      ...zeroMoney(seatCount),
    }
  }

  const pricedLevel = {
    ...level,
    offerPrice: offerPrice ?? level?.offerPrice,
    fee: level?.fee,
    seats: seatCount,
  }
  const fees = resolveTicketBreakdown(
    pricedLevel,
    event?.fee_structure,
    event?.taxRate,
    event
  )
  const payout = round2(fees.payout)
  return {
    name: level?.name || '',
    color: level?.color || '',
    seats: seatCount,
    basePrice: round2(fees.basePrice),
    offerPrice: round2(fees.ticketPrice),
    facilityFee: round2(fees.facilityFee),
    serviceFee: round2(fees.serviceFee),
    processingFee: round2(fees.processingFee),
    salesTax: round2(fees.salesTax),
    chargeBackProtection: round2(fees.chargeBackProtection),
    totalToBuyer: round2(fees.totalToBuyer),
    payout,
    maxPayout: round2(payout * seatCount),
  }
}

function excelWallClock(value, timezone) {
  if (!value) return null
  const zoned = getTimezoneDate(value, timezone)
  if (!zoned?.isValid?.()) return null
  return new Date(
    zoned.year(),
    zoned.month(),
    zoned.date(),
    zoned.hour(),
    zoned.minute(),
    zoned.second()
  )
}

export function exportBaseName(eventName) {
  const safe = String(eventName || 'Event')
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  return `${safe || 'Event'} – Offer breakdown`
}

/**
 * Seats in the price table are the price-level seat counts from Pricing & Fees.
 * Max payout is those seats times your payout per ticket (a sell-out figure).
 * The inventory set count is separate and is left blank for open offers.
 * On-sale times use the event time zone.
 */
export function buildOfferExport({
  event,
  offers,
  offerId,
  getOfferStatus,
  getOfferPrice,
  eventStatusLabel,
  openInventory,
  holds,
  kills,
  tickets,
  exportedAt = new Date(),
}) {
  const timezone = event?.timezone
  const zone = timezoneAbbreviation(event?.start || exportedAt, timezone)
  const ordered = offersInPageOrder(offers, timezone).filter((offer) =>
    offerId == null ? true : String(offer.id) === String(offerId)
  )
  const globalTicketLimit =
    event?.globalTicketLimit === 0 || event?.globalTicketLimit
      ? Number(event.globalTicketLimit)
      : null

  const eventWhen = getTimezoneDate(event?.start, timezone)
  const eventDateLabel = eventWhen?.isValid?.()
    ? `${formatDateTime(eventWhen, 'dateOnly')} • ${formatDateTime(eventWhen, 'timeOnly')}${zone ? ` ${zone}` : ''}`
    : ''
  const exportedWhen =
    getTimezoneDate(exportedAt, timezone) || moment(exportedAt)
  const exportedDateLabel = exportedWhen.format('MMM D, YYYY')

  const inventoryContext = { holds, kills, tickets, openInventory }

  const rows = ordered.map((offer) => {
    const starts = saleBound(offers, offer, 'starts')
    const ends = saleBound(offers, offer, 'ends')
    const status = getOfferStatus?.({ starts, ends }, offer)
    const isOpen = String(offer?.inventoryType || '').toLowerCase() === 'open'
    const codes = accessCodesFor(offer)
    const limit = ticketLimitLabel(offer, globalTicketLimit)
    const inventory = inventorySet(offer, inventoryContext)
    const levels = pricingLevels(offer)
    const priceLevels = levels.map((level, index) =>
      priceLevelRow(level, offer, event, getOfferPrice, levels, index)
    )
    const totalSeats = priceLevels.reduce((sum, level) => sum + level.seats, 0)
    const totalMaxPayout = round2(
      priceLevels.reduce((sum, level) => sum + level.maxPayout, 0)
    )
    const windowLabel =
      starts || ends
        ? `${formatWindowPart(starts, timezone) || EMPTY} → ${formatWindowPart(ends, timezone) || EMPTY}`
        : ''

    return {
      id: offer?.id,
      name: offer?.name || '',
      type: isOpen ? 'Open' : 'Exclusive',
      statusLabel: status?.label || '',
      complimentary: Boolean(offer?.freeOffer),
      windowLabel,
      onSaleStart: excelWallClock(starts, timezone),
      onSaleEnd: excelWallClock(ends, timezone),
      inventory,
      accessCodes: codes,
      accessCodeLabel: accessCodeLabel(codes),
      accessCodeExcel: codes.join(', '),
      ticketLimitLabel: limit,
      hidden: Boolean(offer?.isHidden),
      hiddenLabel: offer?.isHidden ? 'Yes' : 'No',
      priceLevels,
      totalSeats,
      totalMaxPayout,
    }
  })

  return {
    eventName: event?.name || '',
    eventDateLabel,
    eventStatusLabel: eventStatusLabel || '',
    timezone: timezone || '',
    timezoneAbbreviation: zone,
    exportedDateLabel,
    offerCount: rows.length,
    globalTicketLimit,
    globalTicketLimitLabel:
      globalTicketLimit == null ? EMPTY : String(globalTicketLimit),
    fileBaseName: exportBaseName(event?.name),
    offers: rows,
    empty: EMPTY,
    currency: formatCurrency,
  }
}
